// src/scenes/companies/CompanyPlans.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import { tokens } from "../../theme";
import { toast } from "react-toastify";
import Header from "../../components/Header";

const initialForm = { title: "", description: "", terms: "", weblink: "" };

const CompanyPlans = () => {
  const { id: companyId } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog states
  const [addEditOpen, setAddEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [deleteId, setDeleteId] = useState(null);

  /** ------------ FETCH PLANS ------------ */
  const fetchPlans = useCallback(() => {
    setLoading(true);
    axiosInstance
      .get(`/companies/${companyId}/plans`)
      .then((res) => setPlans(res.data || []))
      .catch(() => toast.error("Failed to load plans"))
      .finally(() => setLoading(false));
  }, [companyId]); // Add companyId as dependency

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]); // Now includes fetchPlans in dependencies

  /** ------------ ADD / EDIT ------------ */
  const openAddEdit = (row = null) => {
    setEditing(row);
    setFormData(
      row
        ? {
            title: row.title,
            description: row.description,
            terms: row.terms,
            weblink: row.weblink,
          }
        : initialForm,
    );
    setAddEditOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleConfirmSave = () => {
    if (!formData.title.trim() || !formData.description.trim())
      return toast.warn("Title and Description are required");
    setConfirmOpen(true);
  };

  const submitAddEdit = () => {
    setLoading(true);
    const request = editing
      ? axiosInstance.put(
          `/companies/${companyId}/plans/${editing.id}`,
          formData,
        )
      : axiosInstance.post(`/companies/${companyId}/plans`, formData);

    request
      .then(() => {
        toast.success(`Plan ${editing ? "updated" : "added"} successfully`);
        fetchPlans();
        setAddEditOpen(false);
      })
      .catch(() => toast.error(`Failed to ${editing ? "update" : "add"} plan`))
      .finally(() => {
        setLoading(false);
        setConfirmOpen(false);
      });
  };

  /** ------------ DELETE ------------ */
  const openDelete = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const submitDelete = () => {
    setLoading(true);
    axiosInstance
      .delete(`/companies/${companyId}/plans/${deleteId}`)
      .then(() => {
        toast.success("Plan deleted");
        fetchPlans();
      })
      .catch(() => toast.error("Failed to delete plan"))
      .finally(() => {
        setLoading(false);
        setDeleteOpen(false);
        setDeleteId(null);
      });
  };

  /** ------------ DATA GRID ------------ */
  const columns = useMemo(
    () => [
      { field: "title", headerName: "Title", flex: 1, minWidth: 150 },
      {
        field: "description",
        headerName: "Description",
        flex: 2,
        minWidth: 250,
      },
      { field: "terms", headerName: "Terms", flex: 2, minWidth: 250 },
      {
        field: "weblink",
        headerName: "Link",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => (
          <a href={params.value} target="_blank" rel="noopener noreferrer">
            {params.value}
          </a>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box display="flex" gap={1}>
            <IconButton
              color="primary"
              disabled={loading}
              onClick={() => openAddEdit(row)}
            >
              <Edit />
            </IconButton>
            <IconButton
              color="error"
              disabled={loading}
              onClick={() => openDelete(row.id)}
            >
              <Delete />
            </IconButton>
          </Box>
        ),
      },
    ],
    [loading],
  );

  return (
    <Box m="20px">
      <Header title="COMPANY PLANS" subtitle={`Company ID: ${companyId}`} colors={colors}/>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          startIcon={loading ? <CircularProgress size={16} /> : <Add />}
          variant="contained"
          sx={{
            backgroundColor: colors.greenAccent[500],
            color: colors.grey[900],
          }}
          onClick={() => openAddEdit()}
          disabled={loading}
        >
          Add Plan
        </Button>
      </Box>

      <Box
        height="70vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
        }}
      >
        <DataGrid
          rows={plans}
          columns={columns}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          pageSizeOptions={[5, 10, 20]}
          loading={loading}
          initialState={{
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
          }}
        />
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={addEditOpen}
        onClose={() => setAddEditOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editing ? "Edit Plan" : "Add Plan"}</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, mt: 1 }}>
          <TextField
            label="Title"
            name="title"
            fullWidth
            value={formData.title}
            onChange={handleFormChange}
            autoFocus
          />
          <TextField
            label="Description"
            name="description"
            fullWidth
            multiline
            rows={4}
            value={formData.description}
            onChange={handleFormChange}
          />
          <TextField
            label="Terms"
            name="terms"
            fullWidth
            multiline
            rows={3}
            value={formData.terms}
            onChange={handleFormChange}
          />
          <TextField
            label="Web Link"
            name="weblink"
            fullWidth
            value={formData.weblink}
            onChange={handleFormChange}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddEditOpen(false)}
            disabled={loading}
            color="error"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSave}
            disabled={loading}
            sx={{
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[900],
            }}
          >
            {loading ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Confirm */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Save this plan?</DialogTitle>
        <DialogActions>
          <Button
            color="error"
            onClick={() => setConfirmOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={submitAddEdit}
            disabled={loading}
            sx={{
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[900],
            }}
          >
            {loading ? <CircularProgress size={18} /> : "Yes, Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete this plan?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={submitDelete}
            disabled={loading}
            sx={{ color: colors.redAccent[500] }}
          >
            {loading ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyPlans;